import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faCreditCard,
  faExternalLinkAlt,
  faFileContract,
  faInfoCircle,
  faLock,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import api from '../../../services/api';
import { formatCurrency } from '../../../utils/numberFormatter';

const resolveContractUrl = (contractData) =>
  contractData?.url ||
  contractData?.document_url ||
  contractData?.documentUrl ||
  contractData?.file_url ||
  '';

const PaymentConfirmationStep = ({ flowData, stepStatuses, onNext, searchParams }) => {
  const [hasAutoActivated, setHasAutoActivated] = useState(false);
  const [requestData, setRequestData] = useState(null);
  const [contractData, setContractData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contractLoading, setContractLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentError, setPaymentError] = useState('');
  const [isWidgetLoading, setIsWidgetLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const [isCardSaved, setIsCardSaved] = useState(false);
  const [signatureError, setSignatureError] = useState('');
  const [isInitiatingSignature, setIsInitiatingSignature] = useState(false);
  const [hasProcessedExternalReturn, setHasProcessedExternalReturn] = useState(false);

  const currentStepName = 'PaymentConfirmationStep';
  const customerId = flowData?.tokenCustomerId;
  const requestId = flowData?.tokenRequestId;
  const scriptSrc = process.env.REACT_APP_CIRENIO_PSP_SDK_SRC;
  const publicKey = process.env.REACT_APP_CIRENIO_PSP_PUBLIC_KEY;

  useEffect(() => {
    const currentStepStatus = stepStatuses[currentStepName];

    if (currentStepStatus === 'pending' && !hasAutoActivated) {
      setHasAutoActivated(true);
      onNext(
        {
          step_name: currentStepName,
          step_status: 'in_progress',
          auto_activated: true,
          activation_timestamp: new Date().toISOString()
        },
        'in_progress'
      );
    }
  }, [currentStepName, hasAutoActivated, onNext, stepStatuses]);

  useEffect(() => {
    if (!customerId || !requestId) {
      return;
    }

    let ignore = false;

    const loadRequestData = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/customers/${customerId}/requests/${requestId}`);

        if (!ignore) {
          setRequestData(response.data);
        }
      } catch (requestError) {
        if (!ignore) {
          setError('No se pudo cargar el resumen de la operación.');
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };

    loadRequestData();

    return () => {
      ignore = true;
    };
  }, [customerId, requestId]);

  useEffect(() => {
    if (!requestData || contractData) {
      return;
    }

    let ignore = false;

    const generateContract = async () => {
      try {
        setContractLoading(true);
        const response = await api.post(
          `/customers/${customerId}/requests/${requestId}/generate-contract`
        );

        if (!ignore) {
          setContractData(response.data?.contract || null);
        }
      } catch (contractError) {
        if (!ignore) {
          setError('No se pudo generar el contrato para esta operación.');
        }
      } finally {
        if (!ignore) {
          setContractLoading(false);
        }
      }
    };

    generateContract();

    return () => {
      ignore = true;
    };
  }, [contractData, customerId, requestData, requestId]);

  useEffect(() => {
    if (!scriptSrc || !publicKey || !customerId || !requestId || isCardSaved) {
      return;
    }

    const script = document.createElement('script');
    script.src = scriptSrc;
    script.async = true;

    script.onload = () => {
      if (!window.CirenioPayments) {
        setPaymentError('No se pudo inicializar el medio de pago.');
        return;
      }

      const payments = new window.CirenioPayments(publicKey, customerId, {
        containerId: 'mobile-payment-confirmation-container',
        externalId: `pr-${customerId}-${requestId}`
      });

      payments.on('widget_start', () => setIsWidgetLoading(false));
      payments.on('tokenization_error', () => {
        setIsSubmitting(false);
        setPaymentError('Hubo un problema al procesar tu tarjeta. Intentá nuevamente.');
      });
      payments.on('tokenization_success', async (cardData) => {
        await saveCardToken(cardData);
      });

      payments.openWidget();
    };

    script.onerror = () => {
      setPaymentError('No se pudo cargar el widget de pagos.');
      setIsWidgetLoading(false);
    };

    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [customerId, isCardSaved, publicKey, requestId, reloadTrigger, scriptSrc]);

  useEffect(() => {
    const fromStep = searchParams?.get('from_step');
    const currentStepStatus = stepStatuses[currentStepName];

    if (fromStep !== 'identity_validation' || currentStepStatus !== 'in_progress' || hasProcessedExternalReturn) {
      return;
    }

    const url = new URL(window.location.href);
    url.searchParams.delete('from_step');
    window.history.replaceState({}, document.title, url.toString());
    setHasProcessedExternalReturn(true);

    const flowType = flowData?.flow_type || 'standard';
    const stepStatus = flowType === 'no_biometric' ? 'completed' : 'processed';

    onNext(
      {
        step_name: currentStepName,
        step_status: stepStatus,
        external_signature_completed: true,
        completion_timestamp: new Date().toISOString(),
        completed_from_external: true,
        flow_type: flowType
      },
      stepStatus
    );
  }, [currentStepName, flowData?.flow_type, hasProcessedExternalReturn, onNext, searchParams, stepStatuses]);

  const saveCardToken = async (cardData) => {
    if (!cardData?.source) {
      setPaymentError('Datos de tarjeta incompletos. Intentá nuevamente.');
      return;
    }

    setIsSubmitting(true);
    setPaymentError('');

    try {
      const source = cardData.source;
      await api.post(`/customers/${customerId}/cards`, {
        request_id: requestId,
        token: cardData.token,
        holder: {
          id: source.holder?.identification,
          name: source.holder?.name
        },
        expiration: source.expiration,
        last_four: source.last_4,
        type: source.type,
        variant: source.variant,
        brand: {
          name: source.brand?.name,
          logo: source.brand?.logo_url
        },
        bank: {
          color: source.bank?.color || '',
          logo: source.bank?.logo_url || '',
          name: source.bank?.name || ''
        }
      });

      localStorage.setItem('temp_card_last_four', source.last_4 || '');
      localStorage.setItem('temp_card_brand_name', source.brand?.name || '');
      setIsCardSaved(true);
    } catch (requestError) {
      setPaymentError('No se pudo guardar la tarjeta. Intentá nuevamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const reloadWidget = () => {
    setPaymentError('');
    setIsWidgetLoading(true);
    setIsSubmitting(false);
    setReloadTrigger((prev) => prev + 1);
  };

  const fetchSignatureLink = async () => {
    try {
      const response = await api.get(
        `/customers/${customerId}/requests/${requestId}/self-service/identity-validation`
      );

      if (response.data?.success) {
        return {
          verificationUrl: response.data.data?.verification_url || null,
          testingUrl: response.data.data?.testing_url || null
        };
      }

      return {
        verificationUrl: null,
        testingUrl: null
      };
    } catch (requestError) {
      return {
        verificationUrl: null,
        testingUrl: requestError.response?.data?.data?.testing_url || null,
        error: requestError.response?.data?.message || 'No se pudo iniciar la validación de identidad.'
      };
    }
  };

  const handleValidateAndSign = async () => {
    if (isInitiatingSignature || !isCardSaved) {
      return;
    }

    setIsInitiatingSignature(true);
    setSignatureError('');

    try {
      const result = await fetchSignatureLink();
      const signatureUrl = result.verificationUrl || result.testingUrl;

      if (!signatureUrl) {
        setIsInitiatingSignature(false);
        setSignatureError(result.error || 'No se pudo iniciar la firma.');
        return;
      }

      onNext(
        {
          step_name: currentStepName,
          step_status: 'in_progress',
          external_signature_started: true,
          signature_timestamp: new Date().toISOString(),
          signature_link: signatureUrl,
          testing_mode: Boolean(result.testingUrl && !result.verificationUrl)
        },
        'in_progress'
      );

      window.location.href = signatureUrl;
    } catch (requestError) {
      setIsInitiatingSignature(false);
      setSignatureError('Ocurrió un error al iniciar el proceso de firma.');
    }
  };

  const openContract = () => {
    const contractUrl = resolveContractUrl(contractData);

    if (contractUrl) {
      window.open(contractUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const requestSummary = requestData?.summary || requestData?.request || requestData || {};
  const amount = Number(
    requestSummary.amount ||
      requestSummary.total_amount ||
      requestSummary.financed_amount ||
      requestSummary.requested_amount ||
      0
  );
  const installmentAmount = Number(
    requestSummary.installment_amount || requestSummary.installment || requestSummary.fee || 0
  );
  const installments = Number(requestSummary.installments || requestSummary.terms || 0);

  if (loading) {
    return (
      <div className="container py-4 text-center">
        <FontAwesomeIcon icon={faSpinner} spin className="mb-3 text-primary" size="2x" />
        <p className="mb-0 text-muted">Estamos preparando tu confirmación final.</p>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <div className="text-center mb-4">
        <h4 className="fw-bold mb-2">Pago inicial y confirmación</h4>
        <p className="text-muted mb-0">
          Completá tu medio de pago y luego validá identidad para dejar la operación confirmada.
        </p>
      </div>

      {error && <div className="alert alert-warning">{error}</div>}
      {paymentError && (
        <div className="alert alert-warning">
          {paymentError}{' '}
          {!isCardSaved && (
            <button type="button" className="btn btn-link p-0 align-baseline" onClick={reloadWidget}>
              Reintentar
            </button>
          )}
        </div>
      )}
      {signatureError && <div className="alert alert-warning">{signatureError}</div>}

      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body p-4">
          <div className="d-flex align-items-center mb-3">
            <FontAwesomeIcon icon={faInfoCircle} className="text-primary me-2" />
            <h5 className="mb-0">Resumen de la operación</h5>
          </div>
          <div className="row g-3">
            <div className="col-6">
              <div className="small text-muted">Monto</div>
              <div className="fw-semibold">{formatCurrency(amount)}</div>
            </div>
            <div className="col-6">
              <div className="small text-muted">Cuotas</div>
              <div className="fw-semibold">{installments || '-'} cuotas</div>
            </div>
            <div className="col-12">
              <div className="small text-muted">Valor de cuota</div>
              <div className="fw-semibold">{formatCurrency(installmentAmount)}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body p-4">
          <div className="d-flex align-items-center mb-3">
            <FontAwesomeIcon icon={faCreditCard} className="text-primary me-2" />
            <h5 className="mb-0">Medio de pago</h5>
          </div>

          {!isCardSaved ? (
            <>
              {isWidgetLoading && (
                <div className="text-center text-muted mb-3">
                  <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                  Cargando medio de pago...
                </div>
              )}
              <div id="mobile-payment-confirmation-container" />
              <p className="small text-muted mt-3 mb-0">
                <FontAwesomeIcon icon={faLock} className="me-2" />
                Tus datos se procesan de forma segura con el PSP configurado.
              </p>
            </>
          ) : (
            <div className="alert alert-success mb-0">
              <FontAwesomeIcon icon={faCheckCircle} className="me-2" />
              Tarjeta registrada correctamente. Ya podés continuar con la validación y firma.
            </div>
          )}
        </div>
      </div>

      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body p-4">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
            <div className="d-flex align-items-center">
              <FontAwesomeIcon icon={faFileContract} className="text-primary me-2" />
              <h5 className="mb-0">Contrato</h5>
            </div>
            {resolveContractUrl(contractData) && (
              <button type="button" className="btn btn-outline-primary btn-sm" onClick={openContract}>
                <FontAwesomeIcon icon={faExternalLinkAlt} className="me-2" />
                Ver contrato
              </button>
            )}
          </div>

          {contractLoading ? (
            <div className="text-muted">
              <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
              Generando contrato...
            </div>
          ) : (
            <p className="text-muted mb-0">
              Cuando confirmes, te vamos a redirigir a la validación de identidad para firmar la operación.
            </p>
          )}
        </div>
      </div>

      <button
        type="button"
        className="btn btn-primary w-100 py-3"
        onClick={handleValidateAndSign}
        disabled={!isCardSaved || contractLoading || isSubmitting || isInitiatingSignature}
      >
        {isInitiatingSignature ? 'Iniciando validación...' : 'Validar identidad y firmar'}
      </button>
    </div>
  );
};

export default PaymentConfirmationStep;
